import React, { useState, useEffect, useCallback } from "react";
import { get, del } from "../../../services/api";
import FaceRegistrationModal from "./FaceRegistrationModal";

const FaceRegistrationTab = ({ selectedStudent }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const rollNo = selectedStudent?.code;

  const fetchRegistration = useCallback(async () => {
    if (!rollNo) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const res = await get(`https://presenceiq.inspirertechnologies.com/api/persons/${rollNo}`);
      if (res && res.id) {
        setRegistrationData(res);
      } else {
        setRegistrationData(null);
      }
    } catch (err) {
      // Typically returns 404 naturally when no record exists.
      setRegistrationData(null);
    } finally {
      setIsLoading(false);
    }
  }, [rollNo]);

  useEffect(() => {
    fetchRegistration();
  }, [fetchRegistration]);

  const handleDelete = async () => {
    if (!rollNo) return;
    setIsDeleting(true);
    try {
      await del(`https://presenceiq.inspirertechnologies.com/api/persons/${rollNo}`);
      setRegistrationData(null);
      setShowDeleteConfirm(false);
    } catch (err) {
      alert("Failed to delete record. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    // Refresh the registration data after closing the modal just in case the user registered the student
    fetchRegistration();
  };

  return (
    <div className="face-reg-tab">
      <div className="position-relative">
        <div className="d-flex flex-column h-100 justify-content-center">
          <h4 className="card-title fw-bold mb-3 color-primary">Face Registration System</h4>
          <p className="card-text text-muted mb-4">
            Register facial data for <strong>{selectedStudent?.firstName || "this student"}</strong> to enable seamless, AI-powered automated attendance tracking. This system captures 5 distinct facial angles to ensure high reliability.
          </p>

          <div style={{ minHeight: '80px' }}>
            {isLoading ? (
              <div className="d-flex align-items-center py-3">
                <div className="spinner-border spinner-border-sm text-primary me-3" role="status"></div>
                <span className="text-muted fw-semibold mb-0">Checking registration status...</span>
              </div>
            ) : registrationData ? (
              <div className="bg-success-subtle border border-success-subtle rounded-4 p-3 d-flex flex-wrap align-items-center justify-content-between gap-3 shadow-sm">
                <div>
                  <h6 className="fw-bold mb-2 text-success">
                    <i className="fa fa-check-circle me-2"></i>Student Already Registered
                  </h6>
                  <ul className="list-unstyled mb-0 small text-dark ms-4 opacity-75 fw-medium">
                    <li className="mb-1"><strong>Registered ID:</strong> {registrationData.id}</li>
                    <li className="mb-1"><strong>Name:</strong> {registrationData.name}</li>
                    <li className="mb-1"><strong>Registered On:</strong> {new Date(registrationData.createdOn).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </li>
                  </ul>
                </div>
                <div>
                  <button 
                    className="btn btn-outline-danger shadow-sm rounded-pill px-4 py-2 fw-semibold"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <i className="fa fa-trash me-2"></i>Delete Record
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <button 
                  className="btn btn-primary btn-lg rounded-pill px-4 shadow-sm fw-semibold"
                  onClick={() => setIsModalOpen(true)}
                >
                  <i className="fa fa-id-badge me-2"></i>
                  Open Registration Form
                </button>
              </div>
            )}
          </div>

          <div className="d-flex align-items-center mt-5 pt-3 border-top">
            <div className="me-2">
              <span className="badge small bg-success-subtle text-success py-2 px-3 rounded-pill border border-success-subtle">
                <i className="fa fa-shield me-2"></i>Secure
              </span>
            </div>
            <div className="me-2">
              <span className="badge small bg-primary-subtle text-primary py-2 px-3 rounded-pill border border-primary-subtle">
                <i className="fa fa-bolt me-2"></i>Fast Processing
              </span>
            </div>
            <div>
              <span className="badge small bg-info-subtle text-info py-2 px-3 rounded-pill border border-info-subtle">
                <i className="fa fa-robot me-2"></i>AI Powered
              </span>
            </div>
          </div>
        </div>
      </div>

      <FaceRegistrationModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        selectedStudent={selectedStudent}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060, backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => !isDeleting && setShowDeleteConfirm(false)}></div>
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1065 }} role="dialog">
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }}>
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
                <div className="modal-body p-4 pt-5 text-center position-relative">
                  <button 
                    type="button" 
                    className="btn-close position-absolute top-0 end-0 m-3" 
                    disabled={isDeleting}
                    onClick={() => setShowDeleteConfirm(false)}
                  ></button>
                  <div className="mb-4">
                    <div className="icon-circle bg-danger-subtle text-danger mx-auto d-flex align-items-center justify-content-center" style={{ width: '72px', height: '72px', borderRadius: '50%' }}>
                      <i className="fa fa-trash fa-2x"></i>
                    </div>
                  </div>
                  <h4 className="fw-bold text-dark mb-3">Delete Face Record?</h4>
                  <p className="text-muted small mb-4 px-2">
                    Are you sure you want to delete the facial recognition data for <strong>{selectedStudent?.firstName || "this student"}</strong>? This action is permanent and they will no longer be tracked via facial attendance.
                  </p>
                  <div className="d-flex gap-3 w-100 mt-2">
                    <button 
                      className="btn btn-light fw-semibold flex-fill rounded-pill py-2 text-dark border-secondary bg-transparent" 
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      style={{ border: '1px solid #dee2e6' }}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn btn-danger fw-semibold flex-fill rounded-pill py-2 shadow-sm" 
                      onClick={handleDelete}
                      disabled={isDeleting}
                    >
                      {isDeleting ? (
                         <><span className="spinner-border spinner-border-sm me-2"></span>Deleting...</>
                      ) : (
                        "Yes, Delete Record"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        .face-reg-tab {
          padding: 10px;
        }
        .icon-circle {
          box-shadow: 0 4px 6px rgba(0,0,0,0.05);
        }
        .color-primary {
          color: #0d6efd;
        }
      `}</style>
    </div>
  );
};

export default FaceRegistrationTab;
